import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { StandardDetails } from "../../../sc-models/scope-certificate-details-dto";
import { OptionType } from "../../../../../../shared/components/form-builder/form-config.model";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useParams } from "react-router-dom";
import { useScStore } from "../../../../../../store/scStandard.store";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";


interface StandardDetailsFormProps {
  standardData: StandardDetails[];
  setStandardData: React.Dispatch<React.SetStateAction<StandardDetails[]>>;
  scStatus?: string; // Add SC Status prop
  ccsFlag?: string; // Add CCS Flag prop
  scValidFromDate?: string; // Add SC Valid From Date prop
}

const StandardDetailsForm: React.FC<StandardDetailsFormProps> = ({
  standardData,
  setStandardData,
  scStatus,
  ccsFlag,
  scValidFromDate,
}) => {
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const [data, setData] = useState<StandardDetails[]>(standardData);
  const [tableData, setTableData] = useState<StandardDetails[]>(standardData);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [withdrawalReasonOptions, setWithdrawalReasonOptions] = useState<OptionType[]>([]);
  const [scStandardOptions, setScStandardOptions] = useState<OptionType[]>([]);
  const [standardVersionOptions, setStandardVersionOptions] = useState<OptionType[]>([]);
  const [approvedClaimsOptions, setApprovedClaimsOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { notify } = useNotification();
  const { setScStandards } = useScStore();
  const { setRestrictPopUpSave, isRestrictPopUpSave } = useTransactionCertificateStore();

  // Dynamic validation schema based on SC Status
  const getValidationSchema = (scStatus?: string) => {
    return Yup.object().shape({
      scStandard: Yup.string().required("SC Standard is required"),
      scStandardDescription: Yup.string(),
      scStandardVersion: Yup.string().required("SC Standard Version is required"),
      scStandardAddedDate: Yup.string()
        .required("SC Standard Added Date is required")
        // .test('standard-added-date-sequence', 'SC Standard Added Date cannot be before SC Valid From Date',
        //   function(value) {
        //     if (!value || !scValidFromDate) return true;
        //     return new Date(value) >= new Date(scValidFromDate);
        //   })
        ,
      scStandardRemovedDate: Yup.string()
        // .test('removed-date-sequence', 'SC Standard Removed Date must be on or after SC Standard Added Date',
        //   function(value) {
        //     const addedDate = this.parent.scStandardAddedDate;
        //     if (!addedDate || !value) return true;
        //     return new Date(addedDate) <= new Date(value);
        //   })
        .when([], {
          is: () => scStatus === 'W',
          then: (schema) => schema.required("SC Standard Removed Date is required when SC Status is Withdrawn"),
          otherwise: (schema) => schema.optional()
        }),
      scStandardWithdrawalReason: Yup.array()
        .when([], {
          is: () => scStatus === 'W',
          then: (schema) => schema.required("SC Standard Withdrawal Reason is required when SC Status is Withdrawn"),
          otherwise: (schema) => schema.optional()
        }),
      certifiedOrganizationApprovedClaims: Yup.string()
        .max(4, "Must be at most 4 characters")
        .optional()
        .oneOf(["PC", "CC", "both", "none"], "Invalid value"),

    });
  };

  const validationSchema = getValidationSchema(scStatus);

  // Hardcoded values replaced with API call - see getScStandardValues function below

  // Hardcoded withdrawal reason values - replaced with DTrackIt API
  // const standardWithdrawalReasonValues: OptionType[] = [
  //   { label: "Critical non-conformity", value: "Critical non-conformity", code: "C" },
  //   { label: "Non-conformity", value: "Non-conformity", code: "N" },
  //   { label: "Recertification completed", value: "Recertification completed", code: "R" },
  //   { label: "Withdrawal of the certification body's accreditation", value: "Withdrawal of the certification body's accreditation", code: "W" },
  //   { label: "Administrative", value: "Administrative", code: "A" },
  //   { label: "Voluntary", value: "Voluntary", code: "V" },
  //   { label: "Error", value: "Error", code: "E" },
  //   { label: "Other", value: "Other", code: "O" },
  // ];

  // Fetch SC standard values from DTrackIt API
  const getScStandardValues = async () => {
    try {
      setLoading(true);
      const response = await dtrackItServiceV4.getScStandardValues();
      if (response?.data) {
        const transformedOptions = response.data.map((item: any) => ({
          label: `${item.description_1} (${item.code_1})`,
          value: item.description_1,
          code: item.code_1
        }));
        setScStandardOptions(transformedOptions);
      }
    } catch (error) {
      console.error('Error fetching SC standard values:', error);
      notify('Failed to load SC standard options', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch withdrawal reason values from DTrackIt API
  const getWithdrawalReasonValues = async () => {
    try {
      setLoading(true);
      const response = await dtrackItServiceV4.getStandardWithdrawalReasonValues();
      if (response?.data) {
        const transformedOptions = response.data.map((item: any) => ({
          label: `${item.description_1} (${item.code_1})`,
          value: item.code_1,
          code: item.code_1
        }));
        setWithdrawalReasonOptions(transformedOptions);
      }
    } catch (error) {
      console.error('Error fetching withdrawal reason values:', error);
      notify('Failed to load withdrawal reason options', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Certified Organization Approved Claims values (SCCLMSV4) from DTrackIt API
  const getApprovedClaimsValues = async () => {
    try {
      setLoading(true);
      // Use SCCLMSV4 ref code for Certified Organization Approved Claims (align with other ref code fetches)
      const response = await dtrackItServiceV4.getDTrackItRefCodes('SCCLMSV4');
      if (response?.data) {
        // const allowedCodes = new Set(["PC", "CC", "both", "none"]);
        const transformedOptions = response.data
          // .filter((item: any) => allowedCodes.has(item.code_1))
          .map((item: any) => ({
            label: `${item.description_1} (${item.code_1})`,
            value: item.code_1,
            code: item.code_1,
          }));
        setApprovedClaimsOptions(transformedOptions);
      }
    } catch (error) {
      console.error('Error fetching Approved Claims values:', error);
      notify('Failed to load Approved Claims options', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik<StandardDetails>({
    initialValues: {
      scStandard: "",
      scStandardDescription: "",
      scStandardVersion: "",
      scStandardAddedDate: "",
      scStandardRemovedDate: "",
      scStandardWithdrawalReason: [],
      scStandardWithdrawalReasonDescription: "",
      certifiedOrganizationApprovedClaims: "",
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {

    if(columnId === 'scStandardWithdrawalReason') {
      // Handle array of selected values for multi-select
      const selectedValues = Array.isArray(value) ? value : [];
      const selectedDescriptions = selectedValues.map(val => 
        withdrawalReasonOptions.find(option => option.value === val)?.label || val
      ).join(', ');
      
      formik.setFieldValue('scStandardWithdrawalReason', selectedValues);
      formik.setFieldValue('scStandardWithdrawalReasonDescription', selectedDescriptions);
        setData((prevData) =>
          prevData.map((row, index) =>
          index === rowIndex ? { ...row, ['scStandardWithdrawalReason']: selectedValues, 'scStandardWithdrawalReasonDescription': selectedDescriptions } : row
          )
        );
    } else if(columnId === 'scStandard') {
      // Handle scStandard selection - value should be a code from the table
      const selectedCode = value; // value is the code from the table
      const selectedDescription = String(scStandardOptions.find(option => option.code === value)?.value || "");
      
      formik.setFieldValue('scStandard', selectedCode);
      formik.setFieldValue('scStandardDescription', selectedDescription);
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, ['scStandard']: selectedCode, 'scStandardDescription': selectedDescription } : row
          )
        );
    } else if(columnId === 'scStandardRemovedDate') {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
      // Reset withdrawal reason if removed date is cleared
      if (!value || value.trim() === '') {
        formik.setFieldValue('scStandardWithdrawalReason', []);
        formik.setFieldValue('scStandardWithdrawalReasonDescription', '');
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, 'scStandardWithdrawalReason': [], 'scStandardWithdrawalReasonDescription': '' } : row
          )
        );
      }
    } else if(columnId === 'certifiedOrganizationApprovedClaims') {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    } else {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    }
    
  };

  const handleAddNewRow = () => {
    const newRow: StandardDetails = {
      scStandard: "",
      scStandardDescription: "",
      scStandardVersion: "",
      scStandardAddedDate: new Date().toISOString().split('T')[0],//default to current date
      scStandardRemovedDate: "",
      scStandardWithdrawalReason: [],
      scStandardWithdrawalReasonDescription: "",
      certifiedOrganizationApprovedClaims: "",
    };
    setRestrictPopUpSave(true);
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    // const currentRowValues = formik.values;

    // // Determine if the row being edited is new by comparing `editingRowIndex` with the data length
    // const isNewRow = editingRowIndex === data.length - 1;

    // try {
    //   // Validate the row using the Formik validation schema
    //   await validationSchema.validate(currentRowValues, { abortEarly: false });

    //   if (isNewRow) {
    //     // If validation passes and it's a new row, keep it in the data
    //     setData((prevData) => prevData.map((row, index) => (index === editingRowIndex ? currentRowValues : row)));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // } catch (validationErrors) {
    //   if (isNewRow) {
    //     // If validation fails and it's a new row, remove it from the data
    //     setData((prevData) => prevData.slice(0, -1));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // }
    setRestrictPopUpSave(false);
    setData(tableData);
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = async () => {
    if (editingRowIndex === null) {
      notify('No row is being edited', 'error');
      return;
    }

    // Validate the current row data
    const currentRowData = data[editingRowIndex];
    const hasRemovedDate = currentRowData?.scStandardRemovedDate && currentRowData.scStandardRemovedDate.trim() !== '';
    const hasWithdrawalReason = currentRowData?.scStandardWithdrawalReason && Array.isArray(currentRowData.scStandardWithdrawalReason) && currentRowData.scStandardWithdrawalReason.length > 0;
    
    // Check if withdrawal reason is required but missing
    if (hasRemovedDate && !hasWithdrawalReason) {
      notify('SC Standard Withdrawal Reason is required when SC Standard Removed Date is filled', 'error');
      return;
    }

    // Validate formik form explicitly to avoid stale isValid
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors).length > 0) {
      Object.keys(validationErrors).forEach((errKey) => {
        const errVal: any = (validationErrors as any)[errKey];
        notify(`${errKey}: ${errVal}`, 'error');
      });
      return;
    }

    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);

    const allStandards = data.map(row => row.scStandard);
    setScStandards(allStandards);
    setRestrictPopUpSave(false);
    setStandardData(data); // Update parent state
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  useEffect(() => {
    const currentStore = useScStore.getState();
    
    // Reset the restrict popup save flag on component mount
    setRestrictPopUpSave(false);
    
    // Fetch SC standard values and withdrawal reason values from DTrackIt API
    getScStandardValues();
    getWithdrawalReasonValues();
    getApprovedClaimsValues();
    // Clear version options on mount
    setStandardVersionOptions([]);

    return (()=>{
      setRestrictPopUpSave(false);
    })
  }, []);

  // Fetch standard version values from DTrackIt API based on selected standard
  const getStandardVersions = async (standard: string) => {
    try {
      console.log('Fetching versions for standard:', standard);
      if (!standard) {
        setStandardVersionOptions([]);
        return;
      }
      
      const response = await dtrackItServiceV4.getStandardVersions(standard);
      console.log('Version API response:', response);
      
      if (response?.data) {
        const transformedOptions = response.data.map((item: any) => ({
          label: `${item.description_1}`,
          value: item.code_1,
          code: item.code_1
        }));
        console.log('Transformed version options:', transformedOptions);
        setStandardVersionOptions(transformedOptions);
      } else {
        console.log('No data in response, clearing options');
        setStandardVersionOptions([]);
      }
    } catch (error) {
      console.error('Error fetching standard versions:', error);
      notify('Failed to load standard version options', 'error');
      setStandardVersionOptions([]);
    }
  };

  const columns: EditableColumnDef<StandardDetails>[] = [
    {
      accessorKey: "scStandard",
      header: "SC Standard",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Standard</span>,
      renderEditCell: ({ value, onChange }) => {
        // Handle both cases: value could be a code or a description
        let currentDescription: string = "";
        let currentCode = value;
        
        // Check if value is a description (by matching with option.value)
        const descriptionMatch = scStandardOptions.find(option => option.value === value);
        if (descriptionMatch) {
          // It's a description, use it and find the code
          currentDescription = value;
          currentCode = descriptionMatch.code || "";
        } else {
          // It's a code, find the description
          currentDescription = String(scStandardOptions.find(option => option.code === value)?.value || "");
        }
        
        return (
          <CustomSelect
            name="scStandard"
            value={currentDescription}
            placeholder="Select SC Standard"
            options={scStandardOptions}
            onChange={(e) => {
              // When user selects description, pass the code to the table
              const selectedCode = scStandardOptions.find(option => option.value === e.target.value)?.code || "";
              console.log('Standard selected:', { selectedValue: e.target.value, selectedCode });
              onChange(selectedCode);
              
              // Fetch versions for the selected standard
              if (selectedCode) {
                console.log('Calling getStandardVersions with:', selectedCode);
                getStandardVersions(selectedCode);
              } else {
                console.log('No selected code, clearing version options');
                setStandardVersionOptions([]);
              }
            }}
          />
        );
      },
    },
    {
      accessorKey: "scStandardVersion",
      header: "SC Standard Version",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Standard Version</span>,
      renderEditCell: ({ value, onChange, rowIndex }) => {
        // Check if a standard is selected in the same row - check both tableData and current editing row
        const tableStandard = tableData[rowIndex]?.scStandard;
        const editingStandard = editingRowIndex === rowIndex ? formik.values.scStandard : null;
        const selectedStandard = editingStandard || tableStandard;
        const isDisabled = !selectedStandard || standardVersionOptions.length === 0;
        
        console.log('Version column render:', {
          rowIndex,
          editingRowIndex,
          tableStandard,
          editingStandard,
          selectedStandard,
          versionOptionsLength: standardVersionOptions.length,
          isDisabled,
          versionOptions: standardVersionOptions
        });
        
        return (
          <CustomSelect
            name="scStandardVersion"
            value={value || ""}
            placeholder={isDisabled ? "Select a standard first" : "Select SC Standard Version"}
            options={standardVersionOptions}
            disabled={isDisabled}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      },
    },
    {
      accessorKey: "scStandardAddedDate",
      header: "SC Standard Added Date",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Standard Added Date</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="scStandardAddedDate"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter SC Standard Added Date"
          type="date"
          inputProps={{ 
            min: scValidFromDate || undefined 
          }}
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "scStandardRemovedDate",
      header: "SC Standard Removed Date",
      Header: scStatus === "W" ? <span> <i style={{ color: 'red' }}>* </i>SC Standard Removed Date</span> : "SC Standard Removed Date",
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomTextField
          name="scStandardRemovedDate"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter SC Standard Removed Date"
          type="date"
          // inputProps={{ min: data[rowIndex]?.scStandardAddedDate || undefined }}
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "scStandardWithdrawalReason",
      header: "SC Standard Withdrawal Reason",
      Header: (scStatus === "W" || data.some(row => row.scStandardRemovedDate && row.scStandardRemovedDate.trim() !== '')) 
        ? <span> <i style={{ color: 'red' }}>* </i>SC Standard Withdrawal Reason</span> 
        : "SC Standard Withdrawal Reason",
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomSelect
          name="scStandardWithdrawalReason"
          value={value || []}
          placeholder="Select SC Standard Withdrawal Reason"
          options={withdrawalReasonOptions}
          multiple={true}
          disabled={!data[rowIndex]?.scStandardRemovedDate || data[rowIndex]?.scStandardRemovedDate.trim() === ''}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
          },
      {
        accessorKey: "certifiedOrganizationApprovedClaims",
        header: "Certified Organization Approved Claims",
        renderEditCell: ({ value, onChange }) => (
          <CustomSelect
            name="certifiedOrganizationApprovedClaims"
            value={value}
            placeholder="Select Certified Organization Approved Claims"
            options={approvedClaimsOptions}
            onChange={(e) => {
              onChange(e.target.value)
            }}
          />
        ),
      },

  ];

  const handleEditRow = (rowIndex: number) => {
    setRestrictPopUpSave(true);
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex]);
    
    // Fetch standard versions if a standard is already selected
    const selectedStandard = data[rowIndex]?.scStandard;
    if (selectedStandard) {
      console.log('Fetching versions for existing standard on edit:', selectedStandard);
      getStandardVersions(selectedStandard);
    } else {
      setStandardVersionOptions([]);
    }
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setStandardData(updatedData); // Update parent with updated data
    const remainingStandards = updatedData.map(row => row.scStandard);

    setScStandards(remainingStandards);
  };
//
  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton onClick={handleAddNewRow} sx={{ mb: 2 }} disabled={hasSubmitSC || isRestrictPopUpSave}>
            Add Standard Information
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enableEditing={!hasSubmitSC}
        />
      </Box>
    </div>
  );
};

export default StandardDetailsForm;
